import type { IndustryContent } from '@/types/content';

export const landscapingContent: IndustryContent = {
  slug: 'landscaping',
  name: 'Landscaping',
  headline: 'The Operating System for Landscaping Companies',
  subheadline:
    'Route crews efficiently, track recurring services, and manage seasonal crews—all from one platform built for landscaping pros.',
  heroImageUrl: '/images/industries/landscaping-hero.webp',
  painPoints: [
    {
      title: 'Route planning wastes hours',
      description:
        'Crews drive past each other and backtrack all day. Fuel costs are eating into your margins and customers get inconsistent arrival windows.',
      icon: 'route',
    },
    {
      title: 'Seasonal scaling is painful',
      description:
        'Ramping up crews in spring and scaling down in winter means constant hiring, training, and equipment shuffling.',
      icon: 'calendar',
    },
    {
      title: 'Recurring services slip through',
      description:
        'Weekly mowing, monthly treatments, and seasonal cleanups get missed or double-scheduled. Customers notice and complain.',
      icon: 'recurring',
    },
    {
      title: 'Property details live in heads',
      description:
        "Gate codes, pet warnings, and special instructions are scattered across texts and memories. New crew members show up unprepared.",
      icon: 'notes',
    },
    {
      title: 'Job photos are a mess',
      description:
        "Before/after photos live on techs' personal phones. Good luck finding them when a customer disputes the work.",
      icon: 'camera',
    },
  ],
  features: [
    {
      title: 'Route Optimization',
      description:
        'AI-powered routing that minimizes drive time and fuel costs. Crews hit more stops and finish earlier.',
      icon: 'route',
    },
    {
      title: 'Recurring Service Management',
      description:
        'Weekly, bi-weekly, monthly—set it and forget it. Automatic scheduling with customer notifications.',
      icon: 'recurring',
    },
    {
      title: 'Crew & Equipment Tracking',
      description:
        'Know where every crew and every mower is. Track equipment maintenance and prevent breakdowns.',
      icon: 'equipment',
    },
    {
      title: 'Property Profiles',
      description:
        'Gate codes, irrigation systems, chemical sensitivities—all attached to the property and visible to crews.',
      icon: 'property',
    },
    {
      title: 'Photo Documentation',
      description:
        'Before/after photos attached to every service visit. Build a visual history of every property.',
      icon: 'camera',
    },
    {
      title: 'Mobile-First Experience',
      description:
        'Crews see their routes, job details, and customer notes. Check-in/out, photos, and timekeeping all in one app.',
      icon: 'mobile',
    },
  ],
  testimonial: {
    id: 'landscaping-1',
    quote:
      'We switched from ServiceTitan and saved over $800/month. Plus, the support team actually picks up the phone. It\'s refreshing.',
    author: 'Jennifer Williams',
    role: 'Owner',
    company: 'Williams Landscaping',
    industry: 'landscaping',
    stats: {
      label: 'Monthly Savings',
      value: '$800+',
    },
  },
  stats: [
    { label: 'Fuel Cost Reduction', value: '20%' },
    { label: 'More Stops Per Day', value: '+25%' },
    { label: 'Scheduling Time Saved', value: '60%' },
    { label: 'Customer Retention', value: '94%' },
  ],
  seo: {
    title: 'Landscaping Software | Scheduling & Route Optimization | Omniforge',
    description:
      'Landscaping software built for pros. Route optimization, recurring service management, crew tracking, and mobile app. Start your free trial.',
    keywords: [
      'landscaping software',
      'landscaping scheduling software',
      'landscaping business software',
      'lawn care software',
      'landscaping route optimization',
    ],
  },
};
